import { ExportOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, Card, DatePicker, Result, Space } from 'antd';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import React, { useEffect, useState } from 'react';
import {
  BILLING,
  BILLING_TYPE,
  DEFAULT_DATE_FORMAT,
  HOUR_LOGS_DATE_TIME_FORMAT,
  HOUR_LOGS_TABLE_FIELDS,
  MODULES_KEY,
  PERMISSION_OBJECT,
} from '../../common/constants';
import CommonSelect from '../../components/CommonSelect';
import CommonTable from '../../components/CommonTable';
import VerifyPermissions from '../../components/VerifyPermissions';
import { useTableQueryParams } from '../../hooks/useTableQueryParams';
import { PROJECT_MEMBERS } from '../projects/graphql/Queries';
import { UPDATE_BILLABLE } from './graphql/Mutations';
import { GET_TRACKED_HOUR_LOGS, PROJECTS } from './graphql/Queries';
import './hourLogsHistory.less';

const { RangePicker } = DatePicker;

const buildHourLogsHistoryInput = (params) => ({
  filters: {
    skip: (params?.page - 1) * params?.pageSize,
    limit: params?.pageSize,
  },
  userId: params?.userId,
  projectId: params?.projectId,
  startDate: params?.dateRange?.from,
  endDate: params?.dateRange?.to,
  billable: params?.billable,
  taskBillable: params?.taskBillable,
});

function HourLogsHistory() {
  const { params, setParams } = useTableQueryParams({
    fields: HOUR_LOGS_TABLE_FIELDS,
  });
  const [userSearch, setUserSearch] = useState('');
  const [projectSearch, setProjectSearch] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const isUpdatePermission = VerifyPermissions({
    modulekey: MODULES_KEY?.HOURS_LOG_HISTORY,
    allowedPermissions: [PERMISSION_OBJECT?.update],
  });

  const [executeQuery, { data, loading }] = useLazyQuery(
    GET_TRACKED_HOUR_LOGS,
    {
      fetchPolicy: 'network-only',
      onError() {},
    },
  );

  const [updateBillable] = useMutation(UPDATE_BILLABLE, {
    fetchPolicy: 'network-only',
    onCompleted() {
      executeQuery({
        variables: {
          input: buildHourLogsHistoryInput(params),
        },
      });
      setSelectedRowKeys([]);
    },
    onError() {},
  });

  const handleBillable = (billable) => {
    const input = selectedRowKeys?.map((rowkey) => ({
      billable: billable === BILLING_TYPE?.BILLABLE,
      trackedHourId: rowkey,
    }));
    updateBillable({
      variables: {
        input,
      },
    });
  };

  useEffect(() => {
    if (params?.userId && params?.projectId && params?.dateRange?.from) {
      executeQuery({
        variables: {
          input: buildHourLogsHistoryInput(params),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    if (params?.userId) {
      setUserSearch('');
    }
    if (params?.projectId) {
      setProjectSearch('');
    }
  }, [params?.userId, params?.projectId]);

  const userSelection = (value) => {
    setParams({ userId: value, page: 1 });
  };

  const projectSelection = (value) => {
    setParams({ projectId: value, userId: null, page: 1 });
  };

  const handleUserSearch = debounce((value) => {
    setUserSearch(value?.toString());
  }, 500);

  const handleProjectSearch = debounce((value) => {
    setProjectSearch(value?.toString());
  }, 500);

  const onChange = (pagination, filter) => {
    setParams({
      page: pagination?.pageSize !== params?.pageSize ? 1 : pagination?.current,
      pageSize: pagination?.pageSize,
      billable: !filter?.billable ? null : filter?.billable?.[0],
      taskBillable: !filter?.taskBillable ? null : filter?.taskBillable?.[0],
    });
  };

  const onRangeChange = (dates) => {
    if (dates) {
      setParams({
        dateRange: {
          from: dayjs(dates?.[0])
            .startOf('day')
            .format(HOUR_LOGS_DATE_TIME_FORMAT),
          to: dayjs(dates?.[1])
            .endOf('day')
            .format(HOUR_LOGS_DATE_TIME_FORMAT),
        },
        page: 1,
      });
    }
  };
  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const columns = [
    {
      title: 'Task List',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (value, record) => (
        <>
          <p className="name-column">
            {`${record?.space?.title}>${record?.folder?.title}>${record?.list?.title}`}
          </p>
          <Space>
            {record?.taskName}
            <div
              className="pointer export-icon"
              // eslint-disable-next-line no-undef
              onClick={() => window.open(record?.taskUrl, '_blank')}
            >
              <ExportOutlined />
            </div>
          </Space>
        </>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      align: 'center',
      render: (value) => dayjs(value)?.format(DEFAULT_DATE_FORMAT),
    },
    {
      title: 'Time Tracked',
      dataIndex: 'timeTracked',
      align: 'center',
      key: 'timeTracked',
    },
    {
      title: 'Task Billing Status',
      dataIndex: 'taskBillable',
      key: 'taskBillable',
      align: 'center',
      filters: BILLING,
      filterMultiple: false,
      render: (value) => (value ? 'Billable' : 'Non-Billable'),
    },
    {
      title: 'Billing Status',
      dataIndex: 'billable',
      key: 'billable',
      align: 'center',
      filters: BILLING,
      filterMultiple: false,
      render: (value) => (value ? 'Billable' : 'Non-Billable'),
    },
  ];

  return (
    <>
      <div className="table-card-page hour-logs-history">
        <Card
          title={
            <>
              Hour Logs History
              <Space className="hour-logs-header">
                <div className="hour-logs-filters">
                  <CommonSelect
                    placeholder="Select Project"
                    showSearch
                    query={PROJECTS}
                    onChange={projectSelection}
                    onSearch={handleProjectSearch}
                    responsePath="projectsHoursLogHistory.projects"
                    valuePath="id"
                    labelPath="name"
                    fetchPolicy="network-only"
                    variables={{
                      filters: { searchString: projectSearch },
                    }}
                    useEffectDeps={[projectSearch]}
                    virtual={false}
                  />
                  <RangePicker onChange={onRangeChange} />
                  <CommonSelect
                    placeholder="Select User"
                    showSearch
                    query={PROJECT_MEMBERS}
                    onChange={userSelection}
                    onSearch={handleUserSearch}
                    responsePath="projectMembers.data"
                    valuePath="user.id"
                    labelPath="user.displayName"
                    fetchPolicy="network-only"
                    useEffectDeps={[
                      params?.dateRange,
                      params?.projectId,
                      userSearch,
                    ]}
                    isDataDependent={[params?.dateRange, params?.projectId]}
                    conditionToCheckBeforeQuery={params?.projectId?.length > 0}
                    variables={{
                      where: {
                        id: params?.projectId,
                      },
                      filters: {
                        startDate: params?.dateRange?.from,
                        endDate: params?.dateRange?.to,
                      },
                    }}
                    disabled={!params?.projectId || !params?.dateRange?.from}
                    virtual={false}
                    value={params?.userId}
                  />
                </div>
                <div className="hour-logs-buttons">
                  {isUpdatePermission && (
                    <>
                      <Button
                        type="primary"
                        onClick={() => handleBillable(BILLING_TYPE?.BILLABLE)}
                        disabled={!selectedRowKeys?.length}
                      >
                        Mark As Billable
                      </Button>
                      <Button
                        onClick={() =>
                          handleBillable(BILLING_TYPE?.NON_BILLABLE)
                        }
                        disabled={!selectedRowKeys?.length}
                      >
                        Mark As Non-Billable
                      </Button>
                    </>
                  )}
                </div>
              </Space>
            </>
          }
          className="ant-body-scroll"
        >
          {params?.userId && params?.projectId && params?.dateRange?.from ? (
            <>
              <CommonTable
                size="small"
                className="hours-log-history-table"
                columns={columns}
                dataSource={data?.getTrackedHourLogs?.data}
                loadingData={loading}
                onChange={onChange}
                rowKey={(record) => record?.id}
                rowSelection={
                  isUpdatePermission && {
                    ...rowSelection,
                  }
                }
                paginationConfig={{
                  total: data?.getTrackedHourLogs?.count,
                  current: params?.page,
                  pageSize: params?.pageSize,
                }}
                bordered
              />
            </>
          ) : (
            <Result
              className="no-filters-selected"
              title="Please select employee name, project name and date range for
            which you need to see record."
              extra={<p>Note: All three filters are compulsory to select.</p>}
            />
          )}
        </Card>
      </div>
    </>
  );
}

export default HourLogsHistory;
